import { useState } from "react";
import { Sparkles, Send, Bot } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Btn } from "@/components/admin/ui";

const SUGGESTIONS = [
  "How many workers are active today?",
  "Which cities are below target?",
  "How many meetings were booked?",
  "Show total revenue and sales.",
];

export default function AIOps() {
  const [summary, setSummary] = useState("");
  const [loadingSum, setLoadingSum] = useState(false);
  const [chat, setChat] = useState([]);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);

  const genSummary = async () => {
    setLoadingSum(true);
    try { const { data } = await api.get("/ai/summary"); setSummary(data.summary); }
    catch { setSummary("AI is temporarily unavailable."); }
    finally { setLoadingSum(false); }
  };

  const ask = async (question) => {
    const qq = question || q;
    if (!qq.trim()) return;
    setChat((c) => [...c, { role: "user", text: qq }]);
    setQ(""); setAsking(true);
    try { const { data } = await api.post("/ai/ask", { question: qq }); setChat((c) => [...c, { role: "ai", text: data.answer }]); }
    catch { setChat((c) => [...c, { role: "ai", text: "The AI assistant is temporarily unavailable." }]); }
    finally { setAsking(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="AI Operations" subtitle="Data-grounded summaries and a natural-language assistant. The AI only describes real database metrics." />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-semibold text-white flex items-center gap-2"><Sparkles size={18} className="text-[#1D4ED8]" /> Daily Global Summary</h3>
            <Btn data-testid="ai-generate-summary" onClick={genSummary} disabled={loadingSum}>{loadingSum ? "Analyzing…" : "Generate"}</Btn>
          </div>
          {summary ? (
            <div className="whitespace-pre-wrap text-sm text-slate-300 leading-relaxed bg-[#0B0F19] rounded-lg p-4 border border-white/10" data-testid="ai-summary-output">{summary}</div>
          ) : <p className="text-slate-500 text-sm">Click Generate to produce a data-supported operational summary for today.</p>}
        </Card>

        <Card className="p-5 flex flex-col">
          <h3 className="font-head font-semibold text-white flex items-center gap-2 mb-4"><Bot size={18} className="text-[#06B6D4]" /> Admin AI Assistant</h3>
          <div className="flex-1 space-y-3 min-h-[220px] max-h-80 overflow-y-auto cc-scroll mb-3">
            {chat.length === 0 && (
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => <button key={s} onClick={() => ask(s)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-slate-300 hover:bg-white/10">{s}</button>)}
              </div>
            )}
            {chat.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${m.role === "user" ? "bg-[#1D4ED8] text-white" : "bg-[#0B0F19] border border-white/10 text-slate-200 whitespace-pre-wrap"}`}>{m.text}</div>
              </div>
            ))}
            {asking && <div className="text-slate-500 text-sm">Thinking…</div>}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
            <input data-testid="ai-ask-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about your workforce…"
              className="flex-1 px-4 py-2.5 rounded-lg bg-[#0B0F19] border border-white/10 text-white text-sm focus:border-[#1D4ED8] outline-none" />
            <Btn data-testid="ai-ask-send" type="submit"><Send size={16} /></Btn>
          </form>
        </Card>
      </div>
    </div>
  );
}
